import { z } from "zod";
/*
 * Twin Matching: governed record linking between two observation sides.
 *
 * A matcher is a declarative, versioned definition over a closed operator
 * registry. Executing it produces match candidates in three bands. Review-band
 * candidates are decided by people with an outcome from the matcher's own
 * vocabulary; decisions are append-only and bound to the matcher version and
 * the source snapshot they were shown. Nothing here carries customer-specific
 * vocabulary: outcome ids, labels and materialisation live in the definition.
 */
const slugSchema = z
    .string()
    .trim()
    .regex(/^[a-z][a-z0-9_]{0,62}$/u, "must be a lower-case snake_case identifier");
const fieldKeySchema = z.string().trim().min(1).max(128);
const sha256Schema = z.string().regex(/^[0-9a-f]{64}$/u);
const boundedText = (max) => z.string().trim().min(1).max(max);
export const MATCH_OUTCOME_CLASSES = [
    "equivalence",
    "family",
    "membership",
    "negative",
    "abstain",
];
export const matchOutcomeClassSchema = z.enum(MATCH_OUTCOME_CLASSES);
export const MATCH_BANDS = ["automatic", "review", "abstain"];
export const matchBandSchema = z.enum(MATCH_BANDS);
/** The closed operator registry; every comparator names exactly one entry. */
export const MATCH_OPERATORS = [
    "identifier_equal",
    "normalised_text_equal",
    "trigram_similarity",
    "numeric_tolerance",
    "date_window",
];
export const matchOperatorSchema = z.enum(MATCH_OPERATORS);
export const MATCH_ROW_FILTER_OPERATORS = ["eq", "ne", "in", "not_in", "is_null", "is_not_null"];
const rowFilterScalarSchema = z.union([z.string().max(256), z.number().finite(), z.boolean()]);
/**
 * One predicate that narrows a warehouse side to the rows the matcher is
 * about, such as SUN accounts of one type or a single spreadsheet's rows.
 * Column names are validated as identifiers; values travel as parameters.
 */
export const matchRowFilterSchema = z.object({
    column: slugSchema,
    operator: z.enum(MATCH_ROW_FILTER_OPERATORS),
    value: z.union([rowFilterScalarSchema, z.array(rowFilterScalarSchema).min(1).max(64)]).optional(),
}).strict().superRefine((value, ctx) => {
    const needsList = value.operator === "in" || value.operator === "not_in";
    const needsScalar = value.operator === "eq" || value.operator === "ne";
    if (needsList && !Array.isArray(value.value)) {
        ctx.addIssue({ code: "custom", path: ["value"], message: `${value.operator} requires a list value` });
    }
    if (needsScalar && (value.value === undefined || Array.isArray(value.value))) {
        ctx.addIssue({ code: "custom", path: ["value"], message: `${value.operator} requires a scalar value` });
    }
    if (!needsList && !needsScalar && value.value !== undefined) {
        ctx.addIssue({ code: "custom", path: ["value"], message: `${value.operator} takes no value` });
    }
});
/**
 * A column read from the row's parent through a relationship the connection
 * schema catalog declares (for example an invoice's contact name through
 * `invoice_contact`), exposed on the row under `name` so comparators and
 * contradictions can use it like any other field.
 */
export const matchParentFieldSchema = z.object({
    name: slugSchema,
    relationship: slugSchema,
    column: slugSchema,
}).strict();
/**
 * A field built from several of the row's own columns joined with a
 * separator, such as a person's full name from first name and surname, so a
 * split name can block and compare like a single one.
 */
export const matchComposedFieldSchema = z.object({
    name: slugSchema,
    columns: z.array(slugSchema).min(2).max(6),
    separator: z.string().max(4).default(" "),
}).strict();
export const matchObservationSideSchema = z.discriminatedUnion("kind", [
    z.object({
        kind: z.literal("channel_records"),
        channelId: boundedText(128),
        objectTypeId: boundedText(128),
    }).strict(),
    z.object({
        kind: z.literal("warehouse_relation"),
        schemaName: z.enum(["connector_warehouse", "mlx_curated"]),
        relationName: slugSchema,
        externalIdColumn: slugSchema,
        titleColumn: slugSchema.optional(),
        /** A JSON column whose keys become the row's fields, for relations such as spreadsheet rows. */
        fieldsColumn: slugSchema.optional(),
        /** Predicates that narrow the side to the rows the matcher is about; all must hold. */
        filter: z.array(matchRowFilterSchema).min(1).max(8).optional(),
        /** Parent columns projected onto each row through catalogued relationships. */
        parentFields: z.array(matchParentFieldSchema).min(1).max(8).optional(),
        /** Fields composed from several of the row's own columns. */
        composedFields: z.array(matchComposedFieldSchema).min(1).max(8).optional(),
    }).strict().superRefine((value, ctx) => {
        const names = [
            ...(value.parentFields ?? []).map((field) => field.name),
            ...(value.composedFields ?? []).map((field) => field.name),
        ];
        if (new Set(names).size !== names.length) {
            ctx.addIssue({ code: "custom", path: ["parentFields"], message: "parent and composed field names must be unique" });
        }
    }),
]);
export const matchOutcomeMaterialisationSchema = z.discriminatedUnion("kind", [
    z.object({ kind: z.literal("none") }).strict(),
    z.object({
        kind: z.literal("set_reference_field"),
        /** Which side's native record receives the reference to the other side. */
        side: z.enum(["left", "right"]),
        fieldKey: fieldKeySchema,
    }).strict(),
    /**
     * Record that the left observation is the same entity as the right one in
     * its own system, without touching either record. Equivalence outcomes only.
     */
    z.object({ kind: z.literal("record_identity_link") }).strict(),
    /**
     * Stamp the channel record on `side` with the other side's system and id as
     * its provenance, so the Twin knows where the record came from and later
     * runs treat the pair as already linked. The other side must be a warehouse
     * relation; equivalence outcomes only.
     */
    z.object({ kind: z.literal("record_provenance"), side: z.enum(["left", "right"]) }).strict(),
]);
export const matchOutcomeSchema = z.object({
    id: slugSchema,
    label: boundedText(120),
    class: matchOutcomeClassSchema,
    description: boundedText(1_000).optional(),
    materialisation: matchOutcomeMaterialisationSchema.default({ kind: "none" }),
}).strict();
const comparatorParamsSchema = z.object({
    /** trigram_similarity: minimum similarity that counts as agreement. */
    minimumSimilarity: z.number().min(0).max(1).optional(),
    /** numeric_tolerance: absolute tolerance in the field's unit. */
    absoluteTolerance: z.number().nonnegative().optional(),
    /** numeric_tolerance: relative tolerance as a fraction of the larger value. */
    relativeTolerance: z.number().min(0).max(1).optional(),
    /** numeric_tolerance: compare absolute values; the same as sign "either". */
    absolute: z.boolean().optional(),
    /**
     * numeric_tolerance: the sign the two sources are expected to carry for the
     * same amount. "same" is the default; "opposite" when one side books what
     * the other credits (a spend tracker against supplier bills); "either" when
     * a source is inconsistent and only the magnitude can be trusted.
     */
    sign: z.enum(["same", "opposite", "either"]).optional(),
    /** date_window: maximum distance in days. */
    windowDays: z.number().int().nonnegative().optional(),
}).strict();
export const matchFieldPairSchema = z.object({
    leftField: fieldKeySchema,
    rightField: fieldKeySchema,
}).strict();
/**
 * Blocking keys: exact identifier, normalised text, a name key with legal
 * suffixes stripped ("Acme Holdings Ltd" -> "acme holdings"), or the first N
 * characters of the normalised text for a coarse, deliberately fuzzy block.
 */
export const MATCH_BLOCKING_OPERATORS = [
    "identifier_equal",
    "normalised_text_equal",
    "name_key_equal",
    "prefix_equal",
];
export const matchBlockingRuleSchema = matchFieldPairSchema.extend({
    operator: z.enum(MATCH_BLOCKING_OPERATORS),
    params: z.object({
        /** prefix_equal: how many characters of the normalised text form the key. */
        prefixLength: z.number().int().min(2).max(16).optional(),
    }).strict().default({}),
}).strict().superRefine((value, ctx) => {
    if (value.operator === "prefix_equal" && value.params.prefixLength === undefined) {
        ctx.addIssue({ code: "custom", path: ["params", "prefixLength"], message: "prefix_equal requires params.prefixLength" });
    }
});
export const matchComparatorSchema = matchFieldPairSchema.extend({
    id: slugSchema,
    operator: matchOperatorSchema,
    params: comparatorParamsSchema.default({}),
    /** Zero keeps the comparator out of the score; it still records agreement and can carry a contradiction. */
    weight: z.number().min(0).max(100),
}).strict().superRefine((value, ctx) => {
    const need = (key) => {
        if (value.params[key] === undefined) {
            ctx.addIssue({
                code: "custom",
                path: ["params", key],
                message: `${value.operator} requires params.${key}`,
            });
        }
    };
    if (value.operator === "trigram_similarity")
        need("minimumSimilarity");
    if (value.operator === "date_window")
        need("windowDays");
    if (value.operator === "numeric_tolerance"
        && value.params.absoluteTolerance === undefined
        && value.params.relativeTolerance === undefined) {
        ctx.addIssue({
            code: "custom",
            path: ["params"],
            message: "numeric_tolerance requires absoluteTolerance or relativeTolerance",
        });
    }
});
export const matchBandsSchema = z.object({
    /** Score at or above which a candidate is applied without review; null means every pair is reviewed. */
    automatic: z.number().min(0).max(1).nullable(),
    /** Score at or above which a candidate is queued for review. */
    review: z.number().min(0).max(1),
}).strict().refine((value) => value.automatic === null || value.review <= value.automatic, {
    message: "review threshold must not exceed the automatic threshold",
    path: ["review"],
});
export const MATCHER_DEFINITION_SCHEMA_VERSION = "mlx.twin-matching.matcher.v1";
const AUTOMATIC_OUTCOME_CLASSES = new Set(["equivalence", "family", "membership"]);
export const matcherDefinitionV1Schema = z.object({
    schemaVersion: z.literal(MATCHER_DEFINITION_SCHEMA_VERSION),
    slug: slugSchema,
    label: boundedText(120),
    description: boundedText(2_000).optional(),
    entityType: slugSchema,
    left: matchObservationSideSchema,
    right: matchObservationSideSchema,
    blocking: z.array(matchBlockingRuleSchema).min(1).max(8),
    comparators: z.array(matchComparatorSchema).min(1).max(32),
    /** Comparators whose disagreement is a hard cannot-link regardless of score. */
    contradictions: z.array(slugSchema).max(32).default([]),
    bands: matchBandsSchema,
    outcomes: z.array(matchOutcomeSchema).min(2).max(16),
    /** Outcome applied to automatic-band candidates; required unless the automatic band is off. */
    automaticOutcomeId: slugSchema.optional(),
    /** Maximum candidate pairs one run may emit; the run fails closed above it. */
    maxCandidates: z.number().int().positive().max(1_000_000).default(50_000),
    /** Review-band candidates one run may queue; the rest are stored as deferred. */
    reviewCapPerRun: z.number().int().positive().max(5_000).default(500),
    /** When set, a run whose sides are older than this never auto-decides. */
    freshness: z.object({ maxStalenessMs: z.number().int().positive() }).strict().optional(),
    /**
     * Runs the matcher again on its own once either side has newer data than
     * the last run saw, so fresh rows are matched without anyone pressing Run.
     */
    schedule: z.object({
        onSourceChange: z.boolean().default(true),
        /** Shortest gap between two automatic runs, so a long sync does not start one per page. */
        minIntervalMs: z.number().int().min(60_000).max(86_400_000).default(600_000),
    }).strict().default({ onSourceChange: true, minIntervalMs: 600_000 }),
}).strict().superRefine((value, ctx) => {
    const comparatorIds = value.comparators.map((comparator) => comparator.id);
    if (new Set(comparatorIds).size !== comparatorIds.length) {
        ctx.addIssue({ code: "custom", path: ["comparators"], message: "comparator ids must be unique" });
    }
    if (!value.comparators.some((comparator) => comparator.weight > 0)) {
        ctx.addIssue({ code: "custom", path: ["comparators"], message: "at least one comparator must carry weight" });
    }
    for (const id of value.contradictions) {
        if (!comparatorIds.includes(id)) {
            ctx.addIssue({
                code: "custom",
                path: ["contradictions"],
                message: `contradiction ${id} is not a declared comparator`,
            });
        }
    }
    const outcomeIds = value.outcomes.map((outcome) => outcome.id);
    if (new Set(outcomeIds).size !== outcomeIds.length) {
        ctx.addIssue({ code: "custom", path: ["outcomes"], message: "outcome ids must be unique" });
    }
    if (value.bands.automatic !== null) {
        const automatic = value.outcomes.find((outcome) => outcome.id === value.automaticOutcomeId);
        if (!automatic) {
            ctx.addIssue({
                code: "custom",
                path: ["automaticOutcomeId"],
                message: "automaticOutcomeId must name a declared outcome while the automatic band is enabled",
            });
        }
        else if (!AUTOMATIC_OUTCOME_CLASSES.has(automatic.class)) {
            ctx.addIssue({
                code: "custom",
                path: ["automaticOutcomeId"],
                message: "the automatic outcome must be an equivalence, family or membership outcome",
            });
        }
    }
    if (!value.outcomes.some((outcome) => outcome.class === "negative")) {
        ctx.addIssue({ code: "custom", path: ["outcomes"], message: "a negative outcome is required" });
    }
    if (!value.outcomes.some((outcome) => outcome.class === "abstain")) {
        ctx.addIssue({ code: "custom", path: ["outcomes"], message: "an abstain outcome is required" });
    }
    for (const [index, outcome] of value.outcomes.entries()) {
        if (outcome.materialisation.kind === "record_identity_link" && outcome.class !== "equivalence") {
            ctx.addIssue({
                code: "custom",
                path: ["outcomes", index, "materialisation"],
                message: "record_identity_link requires an equivalence outcome",
            });
        }
        if (outcome.materialisation.kind === "set_reference_field") {
            const side = outcome.materialisation.side === "left" ? value.left : value.right;
            if (side.kind !== "channel_records") {
                ctx.addIssue({
                    code: "custom",
                    path: ["outcomes", index, "materialisation"],
                    message: "set_reference_field requires a channel_records side",
                });
            }
        }
        if (outcome.materialisation.kind === "record_provenance") {
            const owner = outcome.materialisation.side === "left" ? value.left : value.right;
            const other = outcome.materialisation.side === "left" ? value.right : value.left;
            if (outcome.class !== "equivalence") {
                ctx.addIssue({
                    code: "custom",
                    path: ["outcomes", index, "materialisation"],
                    message: "record_provenance requires an equivalence outcome",
                });
            }
            if (owner.kind !== "channel_records" || other.kind !== "warehouse_relation") {
                ctx.addIssue({
                    code: "custom",
                    path: ["outcomes", index, "materialisation"],
                    message: "record_provenance stamps a channel_records side with a warehouse_relation side's identity",
                });
            }
        }
    }
});
export const MATCHER_STATUSES = ["draft", "active", "retired"];
export const matcherStatusSchema = z.enum(MATCHER_STATUSES);
export const matcherRecordSchema = z.object({
    id: z.string().trim().min(1),
    slug: slugSchema,
    version: z.number().int().positive(),
    status: matcherStatusSchema,
    definition: matcherDefinitionV1Schema,
    definitionSha256: sha256Schema,
    createdBy: z.string().trim().min(1),
    createdAt: z.number().int().nonnegative(),
}).strict();
export const matchObservationRefSchema = z.object({
    kind: z.enum(["channel_record", "warehouse_row"]),
    externalId: z.string().trim().min(1).max(512),
    title: z.string().trim().max(512).nullable(),
    fields: z.record(z.string(), z.unknown()),
    /**
     * The ISO currency of each compared amount, by field, where its side declares it: a warehouse relation's declared
     * currency field, or a data set field's fixed currency or the field holding each record's. Absent where nothing
     * declares one, so a reader never guesses.
     */
    currencies: z.record(z.string(), z.string().regex(/^[A-Z]{3}$/u)).optional(),
}).strict();
export const matchFeatureSchema = z.object({
    operator: matchOperatorSchema,
    /** Agreement in [0, 1], or null when either side is missing. */
    agreement: z.number().min(0).max(1).nullable(),
    weight: z.number().min(0),
    left: z.unknown().optional(),
    right: z.unknown().optional(),
}).strict();
export function matcherRecordFromRow(row) {
    return matcherRecordSchema.parse({
        id: row.id,
        slug: row.slug,
        version: Number(row.version),
        status: row.status,
        definition: matcherDefinitionV1Schema.parse(JSON.parse(row.definition_json)),
        definitionSha256: row.definition_sha256,
        createdBy: row.created_by,
        createdAt: Number(row.created_at),
    });
}
export const upsertMatcherRequestSchema = z.object({
    definition: matcherDefinitionV1Schema,
    status: z.enum(["draft", "active"]).default("draft"),
}).strict();
export const listMatchersResponseSchema = z.object({
    matchers: z.array(matcherRecordSchema),
}).strict();
export const matchRunStatsSchema = z.object({
    leftRows: z.number().int().nonnegative(),
    rightRows: z.number().int().nonnegative(),
    pairs: z.number().int().nonnegative(),
    automatic: z.number().int().nonnegative(),
    review: z.number().int().nonnegative(),
    abstain: z.number().int().nonnegative(),
    decided: z.number().int().nonnegative(),
    materialised: z.number().int().nonnegative(),
    materialisationFailures: z.number().int().nonnegative(),
    /** Review-band proposals stored beyond reviewCapPerRun. */
    deferred: z.number().int().nonnegative().default(0),
    /** Automatic-band proposals left open because a side was stale or the policy asks. */
    automaticHeld: z.number().int().nonnegative().default(0),
    /** How many scored pairs each contradiction comparator disagreed on, so a systematic discrepancy shows up. */
    contradictionsByComparator: z.record(z.string(), z.number().int().nonnegative()).default({}),
    /** Of those, how many were the same amount with opposite signs. */
    signFlipsByComparator: z.record(z.string(), z.number().int().nonnegative()).default({}),
    /** Blocked pairs skipped because the channel record's provenance already names the other side. */
    alreadyLinked: z.number().int().nonnegative().default(0),
    /** Blocked pairs skipped because both records are already one entity, which accepting could not change. */
    alreadyOneEntity: z.number().int().nonnegative().default(0),
}).strict();
/** When each side was last observed, and whether the run treated that as stale. */
export const matchRunFreshnessSchema = z.object({
    left: z.object({ asOf: z.number().int().nonnegative().nullable() }).strict(),
    right: z.object({ asOf: z.number().int().nonnegative().nullable() }).strict(),
    stale: z.boolean(),
}).strict();
/** Outcome of one matcher execution; a failed run is a value, not an error. */
export const matchRunResultSchema = z.object({
    batchId: z.string().trim().min(1),
    status: z.enum(["succeeded", "failed"]),
    error: z.string().nullable(),
    stats: matchRunStatsSchema,
    freshness: matchRunFreshnessSchema,
}).strict();
const identitySideSchema = z.object({
    system: boundedText(256),
    externalId: z.string().trim().min(1).max(512),
}).strict();
export const matchIdentityLinkSchema = z.object({
    id: z.string().trim().min(1),
    entityType: slugSchema,
    left: identitySideSchema,
    right: identitySideSchema,
    matcherId: z.string().trim().min(1),
    matcherVersion: z.number().int().positive(),
    decisionId: z.string().trim().min(1),
    createdAt: z.number().int().nonnegative(),
    /** Set once the decision behind the link was undone. */
    revokedAt: z.number().int().nonnegative().nullable().default(null),
}).strict();
export const listIdentityLinksQuerySchema = z.object({
    /** With externalId: links where either side is this observation. */
    system: boundedText(256).optional(),
    externalId: z.string().trim().min(1).max(512).optional(),
    entityType: slugSchema.optional(),
    /** Revoked links are left out unless asked for. */
    includeRevoked: z.enum(["true", "false"]).default("false"),
    limit: z.coerce.number().int().min(1).max(200).default(50),
    cursor: z.string().trim().min(1).max(600).optional(),
}).strict().refine((value) => (value.system === undefined) === (value.externalId === undefined), {
    message: "system and externalId must be given together",
    path: ["externalId"],
});
export const listIdentityLinksResponseSchema = z.object({
    links: z.array(matchIdentityLinkSchema),
    nextCursor: z.string().nullable(),
}).strict();
export const upsertMatcherResponseSchema = z.object({
    matcher: matcherRecordSchema,
    created: z.boolean(),
}).strict();
export const retireMatcherResponseSchema = z.object({
    matcher: matcherRecordSchema,
    /** Open proposals of this version declined by the retirement. */
    declinedProposals: z.number().int().nonnegative(),
}).strict();
/*
 * Shared review (docs/specs/shared-review-reviewers.md): the people who review a matcher's link cards. They are set per
 * slug, outside the definition whose digest is the matcher's id, so changing them mints no version. Each must be an
 * active member who meets the link policy's acceptRole and can read both of the matcher's sources.
 */
const reviewerUserIdSchema = z.string().trim().min(1).max(200);
export const MATCHER_REVIEWER_UNAVAILABLE_REASONS = [
    "not_an_active_member",
    "lost_source_access",
    "below_accept_role",
];
export const matcherReviewerUnavailableSchema = z.object({
    userId: reviewerUserIdSchema,
    reason: z.enum(MATCHER_REVIEWER_UNAVAILABLE_REASONS),
}).strict();
export const matcherReviewersSchema = z.object({
    slug: slugSchema,
    /** Listed and able to review now: the slug's link cards go to them. None listed: anyone who can read both sources. */
    userIds: z.array(reviewerUserIdSchema).max(10),
    /** Listed but unable to review now, and why. */
    unavailable: z.array(matcherReviewerUnavailableSchema).max(10),
}).strict();
export const matcherReviewersResponseSchema = z.object({ reviewers: matcherReviewersSchema }).strict();
export const setMatcherReviewersRequestSchema = z.object({
    /** Up to 10 people; an empty list returns the slug's cards to everyone who can read both sources. */
    userIds: z.array(reviewerUserIdSchema).max(10)
        .refine((values) => new Set(values).size === values.length, "Reviewers must be unique."),
}).strict();
export const setMatcherReviewersResponseSchema = z.object({
    reviewers: matcherReviewersSchema,
    /** The slug's open and deferred link cards now routed to these reviewers. */
    reroutedProposals: z.number().int().nonnegative(),
}).strict();
/*
 * How a warehouse row is named as a record's provenance: the connector it
 * came from, and that connector's own id for the row (the warehouse's
 * source_external_id, "xero:invoice:<id>" for a Xero bill). Records created
 * by an agent from such a row carry the same pair, so a matcher can tell a
 * pair it has already linked from one it still has to judge. Pure, so the
 * link engine and the control plane's materialisation read it the same way.
 */
const CONNECTOR_PREFIXES = [
    ["twin_", "twin"],
    ["xero_", "xero"],
    ["google_sheets_", "google_sheets"],
    ["google_drive_", "google_drive"],
    ["hubspot_", "hubspot"],
    ["stripe_", "stripe"],
];
/** The connector a warehouse relation belongs to, or the relation itself when it is not a connector's. */
export function warehouseSystem(side) {
    const match = CONNECTOR_PREFIXES.find(([prefix]) => side.relationName.startsWith(prefix));
    return match ? match[1] : `${side.schemaName}.${side.relationName}`;
}
/** The provenance a warehouse row would stamp on a record: its source_external_id, else relation plus key. */
export function warehouseRowIdentity(side, row) {
    const sourceExternalId = row.fields.source_external_id;
    const externalId = typeof sourceExternalId === "string" && sourceExternalId.trim() !== ""
        ? sourceExternalId.trim()
        : `${side.relationName}:${row.externalId}`;
    return { system: warehouseSystem(side), externalId };
}
export function sameIdentity(a, b) {
    return Boolean(a) && a.system === b.system && a.externalId === b.externalId;
}
export const twinMatchingPathParamsSchema = z.object({
    matcherId: matcherRecordSchema.shape.id.optional(), matcherSlug: matcherRecordSchema.shape.slug.optional(),
}).strict();
